import type { AdminStructuredLegendSummary } from "./admin-legend-types";
import { getSemanticEnumPresentation, type SemanticEnumRegistry } from "./semantic-enum";

export function readAdminLegendSummaryCount<TSummary extends object>(
  summary: TSummary | null | undefined,
  summaryKey: Extract<keyof TSummary, string>,
): number | null {
  if (!summary) {
    return null;
  }
  const value = (summary as Record<string, unknown>)[summaryKey];
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`ADMIN_LEGEND_SUMMARY_INVALID:${summaryKey}: expected finite number`);
  }
  return value;
}

export function readAdminStructuredLegendSummaryCount<
  TSummary extends AdminStructuredLegendSummary,
>(summary: TSummary | null | undefined, field: string, value: string | undefined): number | null {
  if (!summary) {
    return null;
  }
  const group = summary.groups.find((item) => item.field === field);
  if (!group) {
    throw new Error(`ADMIN_LEGEND_SUMMARY_INVALID:${field}: missing structured summary group`);
  }
  if (typeof group.total !== "number" || !Number.isFinite(group.total)) {
    throw new Error(`ADMIN_LEGEND_SUMMARY_INVALID:${field}.total: expected finite number`);
  }
  if (value === undefined) {
    return group.total;
  }
  const bucket = group.buckets.find((item) => item.value === value);
  if (!bucket) {
    throw new Error(
      `ADMIN_LEGEND_SUMMARY_INVALID:${field}.${value}: missing structured summary bucket`,
    );
  }
  if (typeof bucket.count !== "number" || !Number.isFinite(bucket.count)) {
    throw new Error(`ADMIN_LEGEND_SUMMARY_INVALID:${field}.${value}: expected finite number`);
  }
  return bucket.count;
}

export function readAdminSemanticEnumLegendPresentation<TValue extends string>(
  registry: SemanticEnumRegistry<TValue>,
  value: TValue,
) {
  const presentation = getSemanticEnumPresentation(registry, value);
  if (!presentation) {
    throw new Error(`ADMIN_LEGEND_ENUM_PRESENTATION_INVALID:${value}: missing enum presentation`);
  }
  const rawPresentation = registry[value];
  if (!presentation.legendSemantic && !rawPresentation.tagColor) {
    throw new Error(
      `ADMIN_LEGEND_ENUM_PRESENTATION_INVALID:${value}: expected legendSemantic or explicit tagColor`,
    );
  }
  return {
    semantic: presentation.legendSemantic,
    color: presentation.legendSemantic ? undefined : rawPresentation.tagColor,
  };
}

export function readAdminSemanticEnumLegendSummaryCount<TSummary extends object>(
  group: {
    field: string;
    summary?: TSummary | null;
    summaryField?: string;
    summaryKind?: "flat" | "structured";
  },
  value: string | undefined,
  summaryKey: Extract<keyof TSummary, string>,
): number | null {
  if (group.summaryKind === "structured") {
    return readAdminStructuredLegendSummaryCount(
      group.summary as AdminStructuredLegendSummary | null | undefined,
      group.summaryField ?? group.field,
      value,
    );
  }
  return readAdminLegendSummaryCount(group.summary, summaryKey);
}
